import { clsx } from 'clsx';
import { observer } from 'mobx-react-lite';

import { PadAttributeIcon } from 'src/features/pad-attribute-icon';
import { LoadingTitle } from 'src/shared/components/loading-title/loading-title';
import { useIsOutOfViewport } from 'src/shared/hooks/use-is-out-of-viewport';
import { mergeRefs } from 'src/shared/utils/merge-refs';

import { PadCard } from '../../../pad-card';
import type { PadRigOperation } from '../../presets/drilling-rigs-chart/entities';
import type { RigsViewSettingsProvider } from '../../presets/drilling-rigs-chart/rigs-view-settings-provider';
import { useClickHandler } from '../../shared/use-click-handler';
import { useMovableElement } from '../../shared/use-movable-element';
import type { Viewport } from '../../shared/viewport/viewport';
import { ChartTooltip } from '../chart-tooltip/chart-tooltip';
import { RSEReadinessIcons } from '../rse-readiness-icons';
import { useDragging } from '../sorting/hooks/use-dragging';

import styles from './pad-rig-operation-wrapper.module.scss';
import { PadTooltipInner } from './pad-tooltip-inner';

interface Props {
  item: PadRigOperation;
  horizontalViewport: Viewport;
  verticalViewport: Viewport;
  viewSettingsProvider: RigsViewSettingsProvider;
  isCompactView?: boolean;
  onClick?: VoidFunction;
  className?: string;
  cardClassName?: string;
  headerClassName?: string;
}

export const DraggingPadRigOperationWrapper = observer(function DraggingPadRigOperationWrapper({
  item,
  horizontalViewport,
  verticalViewport,
  onClick,
  className,
  cardClassName,
  headerClassName,
  viewSettingsProvider,
  isCompactView,
}: Props) {
  const { ref: draggingRef } = useDragging({
    id: item.getKey('dragging'),
    item,
  });

  const ref = useMovableElement({
    item,
    horizontalViewport,
    verticalViewport,
  });

  const { onPointerDown, onPointerUp } = useClickHandler(onClick);

  const style = (() => {
    if (isCompactView) {
      const padGradient = viewSettingsProvider.getPadGradient(item);

      if (!padGradient) {
        return {};
      }

      return {
        background: padGradient,
        paddingLeft: 4,
        '--well-group-card-border-color': 'var(--primary-2)',
      };
    }
    return {};
  })();

  const isOutOfViewport = useIsOutOfViewport(item, horizontalViewport);

  const tooltipPlacement = isOutOfViewport ? 'rightTop' : 'top';

  const padAttributes = viewSettingsProvider.getPadAttributes(item);
  const rseReadinessVisibilitySettings = viewSettingsProvider.shownAttributesFields['padSettings'];

  const padIcons = (
    <div className={clsx(styles.iconsContainer, isCompactView && styles.iconsContainer__compact)}>
      <RSEReadinessIcons
        readinessData={item.rseReadiness}
        rseReadinessVisibilitySettings={rseReadinessVisibilitySettings}
      />
      {padAttributes?.map((attributeName) => (
        <PadAttributeIcon key={attributeName} iconName={attributeName} className={styles.icon} />
      ))}
    </div>
  );

  const atributes = viewSettingsProvider.getAttributes(item);

  return (
    <ChartTooltip
      content={
        <PadTooltipInner
          title={<LoadingTitle field={viewSettingsProvider.getTitle(item)} />}
          subTitle={viewSettingsProvider.getPadTooltipDate(item)}
          attributes={viewSettingsProvider.getPadTooltipAttributes(item)}
        />
      }
      placement={tooltipPlacement}
    >
      <div ref={mergeRefs(ref, draggingRef)} className={clsx(className, styles.container)}>
        <PadCard
          title={
            atributes ? (
              <div className={clsx(styles.cardHeaderTitle, isCompactView && styles.cardHeaderTitle__compact)}>
                <p className={clsx('caption', styles.cardHeaderTitleText)}>{atributes?.join(' / ')}</p>
              </div>
            ) : (
              <LoadingTitle field={viewSettingsProvider.getTitle(item)} />
            )
          }
          isCompactView={isCompactView}
          extra={padIcons}
          className={clsx(cardClassName, styles.card)}
          headerClassName={headerClassName}
          style={style}
          onPointerDown={onPointerDown}
          onPointerUp={onPointerUp}
        />
      </div>
    </ChartTooltip>
  );
});
